// PaymentCTA.jsx — v4
// Two payment paths depending on authentication:
//
// AUTHENTICATED (has Blitz account):
//   · Blitz Balance — free, instant
//   · Spending Power — 5% fee, month-end settlement
//   · T2A via Card/PayPal — external rail, credits recipient Blitz balance
//
// UNAUTHENTICATED (external payer, no Blitz account):
//   · Uses the existing public pay-request route (POST /gu-balance/pay-request/<token>)
//   · Invoice must have a unique_code (set by create_request_transfer)
//   · No login required — works like any payment link
//   · Card or PayPal
//
// The recipient resolution (getByUserId) is ONLY called for authenticated users
// because the public pay-request route doesn't need a recipient GUID.

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Stack, Chip, Button,
  CircularProgress, Collapse, Card, CardContent, Avatar,
  Alert, TextField,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  CreditCard as CreditCardIcon,
  Payment as PayPalIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  WarningAmber as WarnIcon,
  OpenInNew as RedirectIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useQuery, useMutation } from 'react-query';
import API from '../../../../API';

// ─── Fee math ─────────────────────────────────────────────────────────────────
const RAILS_FEE = {
  stripe: { pct: 0.035, fixed: 0.30 },
  paypal: { pct: 0.029, fixed: 0.30 },
};
function computeT2AFee(amount, rail = 'stripe') {
  const r = RAILS_FEE[rail] || RAILS_FEE.stripe;
  const gross = (amount + r.fixed) / (1 - r.pct);
  return {
    senderPays:        +gross.toFixed(2),
    recipientReceives: +amount.toFixed(2),
    fee:               +(gross - amount).toFixed(2),
  };
}

// ─── Authenticated recipient resolver (exact FK — no fuzzy search) ────────────
async function resolveRecipientGUID(invoice) {
  if (!invoice) return null;
  try {
    if (invoice.creator_id) {
      const res = await API.globalUsers.getByCreatorUsername(invoice.creator_id);
      const id = res?.id ?? res?.global_user?.id ?? null;
      if (id) return id;
    }
    if (invoice.user_id) {
      const res = await API.globalUsers.getByUserId(invoice.user_id);
      const id = res?.id ?? res?.global_user?.id ?? null;
      if (id) return id;
    }
  } catch (e) {
    // 401 = not logged in, handled by caller
    if (e?.response?.status !== 401) {
      console.warn('[PaymentCTA] resolveRecipientGUID failed:', e?.message);
    }
  }
  return null;
}

// Extract the pay token from the invoice
// unique_code is set by create_request_transfer
// Fallback: parse it from po_number (e.g. "T2R-HXEFBE5TCHY6" → "HXEFBE5TCHY6")
function extractPayToken(invoice) {
  if (invoice?.unique_code) return invoice.unique_code;
  const po = invoice?.po_number || '';
  if (po.startsWith('T2R-')) return po.slice(4).toLowerCase();
  return null;
}

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

// ─── Rail selector pill ───────────────────────────────────────────────────────
function RailPill({ id, label, icon, selected, onSelect }) {
  return (
    <Box
      onClick={() => onSelect(id)}
      sx={{
        flex: 1, py: 1.25, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
        border: '1.5px solid',
        borderColor: selected ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)',
        bgcolor: selected ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.07)',
        transition: 'all 0.18s',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75}>
        <Box sx={{ color: 'white', display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="caption" fontWeight={700} sx={{ color: 'white', fontSize: 11 }}>
          {label}
        </Typography>
        {selected && <CheckCircleIcon sx={{ fontSize: 11, color: 'white' }} />}
      </Stack>
    </Box>
  );
}

// ─── Method card ──────────────────────────────────────────────────────────────
function MethodCard({ selected, disabled, onClick, gradient, border, children }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        borderRadius: 3, border: '2px solid',
        borderColor: selected ? 'transparent' : border,
        background: selected ? gradient : 'rgba(248,250,252,0.6)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 0.22s ease',
        transform: selected ? 'scale(1.008)' : 'scale(1)',
        boxShadow: selected ? '0 8px 28px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        p: 2.25,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const PaymentCTA = ({ invoice, invoiceAmount, variant = 'regular' }) => {
  const amt = parseFloat(invoiceAmount || invoice?.amount_due || 0);

  // Don't render if already paid/cancelled
  const status = invoice?.status?.toLowerCase();
  if (status && status !== 'pending' && status !== 'approved') return null;
  if (!invoice?.id) return null;

  // ── Auth detection ──────────────────────────────────────────────────
  const { data: balData, isLoading: balLoading } = useQuery(
    ['myBalance'],
    () => API.balanceLedger.fetchMyBalance(),
    { retry: false, onError: () => {} }
  );
  const { data: spendData, isLoading: spendLoading } = useQuery(
    ['mySpendingPower'],
    () => API.teams.getMySpendingInfo(),
    { retry: false, onError: () => {} }
  );

  const isAuthenticated = !!balData;
  const senderId   = balData?.user?.id ?? null;
  const balAvail   = parseFloat(balData?.balance || 0);
  const spendAvail = parseFloat(spendData?.total_available_funds || 0);
  const spendFee   = amt * 0.05;
  const totalCredit = amt + spendFee;
  const canBalance  = isAuthenticated && balAvail >= amt;
  const canSpend    = isAuthenticated && spendAvail >= totalCredit;

  // ── State ────────────────────────────────────────────────────────────
  const [method,      setMethod]      = useState('card'); // 'balance'|'credit'|'t2a'|'card'
  const [rail,        setRail]        = useState('stripe');
  const [recipientId, setRecipientId] = useState(null);
  const [guidState,   setGuidState]   = useState('idle');
  const [payerEmail,  setPayerEmail]  = useState('');
  const [done,        setDone]        = useState(false);
  const [busy,        setBusy]        = useState(false);

  const idempKey = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  );

  const payToken = extractPayToken(invoice);
  const calc     = computeT2AFee(amt, rail);

  // ── Auto-select method once auth data loads ───────────────────────
  useEffect(() => {
    if (balLoading) return;
    if (!isAuthenticated) { setMethod('card'); return; }
    if (canBalance) { setMethod('balance'); return; }
    if (canSpend)   { setMethod('credit');  return; }
    setMethod('t2a');
  }, [balLoading, isAuthenticated, canBalance, canSpend]);

  // ── Resolve recipient (authenticated path only) ───────────────────
  useEffect(() => {
    if (!isAuthenticated || balLoading) return;
    setGuidState('loading');
    resolveRecipientGUID(invoice).then((id) => {
      setRecipientId(id);
      setGuidState(id ? 'ok' : 'fail');
    });
  }, [isAuthenticated, balLoading, invoice?.id]);

  // ── PUBLIC card/PayPal payment (unauthenticated) ──────────────────
  // Uses POST /gu-balance/pay-request/<token> — no auth required
  const publicPayMutation = useMutation(
    async () => {
      if (!payToken) throw new Error('No payment token found on this invoice');
      return API.balanceLedger.payRequestByToken(payToken, {
        rail:        rail,
        payer_email: payerEmail.trim() || undefined,
      });
    },
    {
      onSuccess: (data) => {
        const url = data?.approval_url || data?.url;
        if (url) window.location.href = url;
        else setDone(true);
      },
      onError: (err) => {
        const msg = err?.response?.data?.error || err?.message;
        if (msg?.includes('expired') || msg?.includes('paid')) {
          alert('This payment link has expired or the invoice has already been paid.');
        } else {
          alert(msg || 'Payment failed — please try again.');
        }
      },
    }
  );

  // ── AUTHENTICATED T2A (card/PayPal via gift route) ────────────────
  const t2aMutation = useMutation(
    async () => {
      if (!senderId)    throw new Error('Session expired — please refresh and log in again');
      if (!recipientId) throw new Error('Recipient not verified — please refresh the page');
      return API.balanceLedger.t2aGift({
        sender_id:    senderId,
        description:  invoice?.client_notes || invoice?.notes || `Invoice #${invoice?.po_number}`,
        payment_rail: rail,
        fee_payer:    'sender',
        transfers: [{
          type:                     'internal',
          amount:                   String(calc.senderPays),
          recipient_global_user_id: recipientId,
          metadata: {
            t2a:               true,
            agreed_amount:     amt,
            fee_amount:        calc.fee,
            fee_payer:         'sender',
            payment_rail:      rail,
            invoice_id:        invoice?.id,
            invoice_po_number: invoice?.po_number,
          },
        }],
      });
    },
    {
      onSuccess: (data) => {
        const url = data?.approval_url || data?.url;
        if (url) window.location.href = url;
        else setDone(true);
      },
      onError: (err) => {
        alert(err?.response?.data?.error || err?.message || 'Payment failed — please try again.');
      },
    }
  );

  // ── Direct balance/credit pay ─────────────────────────────────────
  const handleDirectPay = async () => {
    setBusy(true);
    try {
      if (method === 'balance') {
        await API.invoices.payWithBalance(invoice.id, { amount: amt, idempotency_key: idempKey.current });
      } else {
        await API.invoices.payWithCredit(invoice.id, { amount: amt, idempotency_key: idempKey.current });
      }
      setDone(true);
    } catch (e) {
      alert(e?.response?.data?.error || 'Payment failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const isBusy = busy || t2aMutation.isLoading || publicPayMutation.isLoading;

  const handlePay = () => {
    if (method === 'balance' || method === 'credit') { handleDirectPay(); return; }
    if (method === 't2a')  { t2aMutation.mutate();     return; }
    if (method === 'card') { publicPayMutation.mutate(); return; }
  };

  const btnDisabled =
    isBusy
    || (method === 'balance' && !canBalance)
    || (method === 'credit'  && !canSpend)
    || (method === 't2a'     && guidState === 'loading')
    || (method === 'card'    && !payToken);

  const btnLabel = (() => {
    if (isBusy)              return 'Processing…';
    if (method === 'balance') return `Pay ${fmt(amt)} — Blitz Balance`;
    if (method === 'credit')  return `Pay ${fmt(totalCredit)} — Spending Power`;
    if (method === 't2a')     return `Pay ${fmt(calc.senderPays)} via ${rail === 'stripe' ? 'Card →' : 'PayPal →'}`;
    return `Pay ${fmt(calc.senderPays)} via ${rail === 'stripe' ? 'Card →' : 'PayPal →'}`;
  })();

  // ── Success ───────────────────────────────────────────────────────
  if (done) {
    return (
      <Box sx={{
        mt: 5, p: 4, borderRadius: 4, textAlign: 'center',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        boxShadow: '0 12px 40px rgba(16,185,129,0.35)',
      }}>
        <CheckCircleIcon sx={{ fontSize: 52, color: 'white', mb: 1.5 }} />
        <Typography variant="h5" fontWeight={800} sx={{ color: 'white', mb: 0.75 }}>
          Payment complete!
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 15 }}>
          {fmt(amt)} has been paid successfully.
        </Typography>
      </Box>
    );
  }

  // ── Payment body ──────────────────────────────────────────────────
  const PaymentBody = () => (
    <Stack spacing={2}>

      {/* ── Authenticated methods ── */}
      {isAuthenticated && (
        <>
          {/* Balance */}
          <MethodCard
            selected={method === 'balance'}
            disabled={!canBalance}
            onClick={() => setMethod('balance')}
            gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
            border="rgba(59,130,246,0.25)"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <WalletIcon sx={{ fontSize: 20, color: method === 'balance' ? 'white' : '#3b82f6', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" fontWeight={700}
                    sx={{ color: method === 'balance' ? 'white' : 'text.primary', fontSize: '0.88rem' }}>
                    Blitz Balance
                  </Typography>
                  <Chip label="No Fees" size="small" sx={{
                    height: 15, fontSize: 9, fontWeight: 700,
                    bgcolor: method === 'balance' ? 'rgba(255,255,255,0.22)' : 'rgba(16,185,129,0.1)',
                    color: method === 'balance' ? 'white' : '#059669',
                  }} />
                  {method === 'balance' && <CheckCircleIcon sx={{ fontSize: 13, color: 'white', ml: 'auto' }} />}
                </Stack>
                <Typography variant="caption" sx={{ color: method === 'balance' ? 'rgba(255,255,255,0.72)' : 'text.secondary', fontSize: 10.5 }}>
                  Instant · zero fees · {fmt(balAvail)} available
                </Typography>
              </Box>
            </Stack>
            {!canBalance && (
              <Typography variant="caption" sx={{ color: '#ef4444', fontSize: 10.5, display: 'block', mt: 1 }}>
                Need {fmt(Math.max(0, amt - balAvail))} more to use balance
              </Typography>
            )}
          </MethodCard>

          {/* Spending Power */}
          <MethodCard
            selected={method === 'credit'}
            disabled={!canSpend}
            onClick={() => setMethod('credit')}
            gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            border="rgba(245,158,11,0.25)"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CreditCardIcon sx={{ fontSize: 20, color: method === 'credit' ? 'white' : '#d97706', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" fontWeight={700}
                    sx={{ color: method === 'credit' ? 'white' : 'text.primary', fontSize: '0.88rem' }}>
                    Spending Power
                  </Typography>
                  <Chip label="5% fee" size="small" sx={{
                    height: 15, fontSize: 9, fontWeight: 700,
                    bgcolor: method === 'credit' ? 'rgba(255,255,255,0.22)' : 'rgba(245,158,11,0.1)',
                    color: method === 'credit' ? 'white' : '#d97706',
                  }} />
                  {method === 'credit' && <CheckCircleIcon sx={{ fontSize: 13, color: 'white', ml: 'auto' }} />}
                </Stack>
                <Typography variant="caption" sx={{ color: method === 'credit' ? 'rgba(255,255,255,0.72)' : 'text.secondary', fontSize: 10.5 }}>
                  Pay now · settle month-end · {fmt(spendAvail)} available
                </Typography>
              </Box>
            </Stack>
          </MethodCard>

          {/* T2A — authenticated card/PayPal */}
          <MethodCard
            selected={method === 't2a'}
            disabled={false}
            onClick={() => setMethod('t2a')}
            gradient="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)"
            border="rgba(139,92,246,0.25)"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PayPalIcon sx={{ fontSize: 20, color: method === 't2a' ? 'white' : '#7c3aed', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" fontWeight={700}
                    sx={{ color: method === 't2a' ? 'white' : 'text.primary', fontSize: '0.88rem' }}>
                    Card or PayPal
                  </Typography>
                  {method === 't2a' && <CheckCircleIcon sx={{ fontSize: 13, color: 'white', ml: 'auto' }} />}
                </Stack>
                <Typography variant="caption" sx={{ color: method === 't2a' ? 'rgba(255,255,255,0.72)' : 'text.secondary', fontSize: 10.5 }}>
                  Stripe or PayPal · small processing fee · credits recipient Blitz balance
                </Typography>
              </Box>
            </Stack>

            <Collapse in={method === 't2a'} unmountOnExit>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.18)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
                  Choose method
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <RailPill id="stripe" label="Card / Bank" icon={<CreditCardIcon sx={{ fontSize: 13 }} />} selected={rail === 'stripe'} onSelect={setRail} />
                  <RailPill id="paypal" label="PayPal" icon={<PayPalIcon sx={{ fontSize: 13 }} />} selected={rail === 'paypal'} onSelect={setRail} />
                </Stack>
                <Box sx={{ py: 1.5, px: 1.75, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', mb: 1.5 }}>
                  <Stack spacing={0.55}>
                    {[['Invoice amount', fmt(amt)], ['Processing fee', `+${fmt(calc.fee)}`]].map(([l, v]) => (
                      <Stack key={l} direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 10.5 }}>{l}</Typography>
                        <Typography variant="caption" fontWeight={600} sx={{ color: 'white', fontSize: 10.5 }}>{v}</Typography>
                      </Stack>
                    ))}
                    <Box sx={{ height: 1, bgcolor: 'rgba(255,255,255,0.18)', my: 0.25 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>You pay</Typography>
                      <Typography fontWeight={800} sx={{ color: 'white', fontSize: '1rem' }}>{fmt(calc.senderPays)}</Typography>
                    </Stack>
                  </Stack>
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  {guidState === 'loading' && (
                    <><CircularProgress size={9} sx={{ color: 'rgba(255,255,255,0.6)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>Verifying recipient…</Typography></>
                  )}
                  {guidState === 'ok' && (
                    <><LockIcon sx={{ fontSize: 10, color: 'rgba(255,255,255,0.65)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 10 }}>
                      Recipient's Blitz balance credited on confirmation
                    </Typography></>
                  )}
                  {guidState === 'fail' && (
                    <><WarnIcon sx={{ fontSize: 10, color: 'rgba(255,220,80,0.9)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,220,80,0.9)', fontSize: 10 }}>
                      Recipient verification failed — refresh or try the public payment option
                    </Typography></>
                  )}
                </Stack>
              </Box>
            </Collapse>
          </MethodCard>
        </>
      )}

      {/* ── Unauthenticated (public) card/PayPal ── */}
      {!isAuthenticated && !balLoading && (
        <MethodCard
          selected={method === 'card'}
          disabled={!payToken}
          onClick={() => setMethod('card')}
          gradient="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)"
          border="rgba(139,92,246,0.25)"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PayPalIcon sx={{ fontSize: 20, color: method === 'card' ? 'white' : '#7c3aed', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}
                sx={{ color: method === 'card' ? 'white' : 'text.primary', fontSize: '0.88rem' }}>
                Pay by Card or PayPal
              </Typography>
              <Typography variant="caption" sx={{ color: method === 'card' ? 'rgba(255,255,255,0.72)' : 'text.secondary', fontSize: 10.5 }}>
                No Blitz account required · Stripe or PayPal
              </Typography>
            </Box>
            <CheckCircleIcon sx={{ fontSize: 13, color: 'white' }} />
          </Stack>

          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.18)' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
              Choose method
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <RailPill id="stripe" label="Card / Bank" icon={<CreditCardIcon sx={{ fontSize: 13 }} />} selected={rail === 'stripe'} onSelect={setRail} />
              <RailPill id="paypal" label="PayPal" icon={<PayPalIcon sx={{ fontSize: 13 }} />} selected={rail === 'paypal'} onSelect={setRail} />
            </Stack>

            {/* Cost breakdown */}
            <Box sx={{ py: 1.5, px: 1.75, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', mb: 1.5 }}>
              <Stack spacing={0.55}>
                {[['Invoice amount', fmt(amt)], ['Processing fee', `+${fmt(calc.fee)}`]].map(([l, v]) => (
                  <Stack key={l} direction="row" justifyContent="space-between">
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 10.5 }}>{l}</Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: 'white', fontSize: 10.5 }}>{v}</Typography>
                  </Stack>
                ))}
                <Box sx={{ height: 1, bgcolor: 'rgba(255,255,255,0.18)', my: 0.25 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>You pay</Typography>
                  <Typography fontWeight={800} sx={{ color: 'white', fontSize: '1rem' }}>{fmt(calc.senderPays)}</Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Optional payer email */}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.75 }}>
              Your email (optional — for receipt)
            </Typography>
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                '& .MuiInputBase-root': {
                  bgcolor: 'rgba(255,255,255,0.12)',
                  borderRadius: 2,
                  color: 'white',
                  fontSize: 13,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                  '&.Mui-focused fieldset': { borderColor: 'white' },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  py: 1,
                  '&::placeholder': { color: 'rgba(255,255,255,0.45)' },
                },
              }}
            >
              <TextField
                fullWidth
                size="small"
                type="email"
                placeholder="you@example.com"
                value={payerEmail}
                onChange={(e) => setPayerEmail(e.target.value)}
                variant="outlined"
              />
            </Box>

            {!payToken && (
              <Typography variant="caption" sx={{ color: 'rgba(255,220,80,0.9)', fontSize: 10.5, display: 'block', mt: 1 }}>
                This invoice doesn't have a payment token — please contact the sender.
              </Typography>
            )}
          </Box>
        </MethodCard>
      )}

      {/* Loading skeleton while auth resolves */}
      {balLoading && (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <CircularProgress size={24} sx={{ color: '#667eea' }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Loading payment options…
          </Typography>
        </Box>
      )}

      {/* Pay button */}
      {!balLoading && (
        <>
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={btnDisabled}
            onClick={handlePay}
            endIcon={
              isBusy
                ? <CircularProgress size={16} sx={{ color: 'white' }} />
                : (method === 't2a' || method === 'card')
                ? <RedirectIcon sx={{ fontSize: 16 }} />
                : <CheckCircleIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              height: 52, borderRadius: 2.5, fontWeight: 800, fontSize: 15,
              background: btnDisabled ? undefined : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: btnDisabled ? 'none' : '0 6px 22px rgba(102,126,234,0.4)',
              '&:hover': { transform: btnDisabled ? 'none' : 'translateY(-1px)' },
              transition: 'all 0.2s ease',
              '&:disabled': { background: 'rgba(148,163,184,0.5)', boxShadow: 'none' },
            }}
          >
            {btnLabel}
          </Button>

          {/* Login nudge for unauthenticated users */}
          {!isAuthenticated && (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', fontSize: 11 }}>
              Have a Blitz account?{' '}
              <Box component="a" href="/login" sx={{ color: '#667eea', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Log in
              </Box>
              {' '}to pay with balance or spending power (no processing fee)
            </Typography>
          )}

          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ opacity: 0.5 }}>
            <LockIcon sx={{ fontSize: 11 }} />
            <Typography variant="caption" sx={{ fontSize: 10 }}>
              Secured by Blitz · Stripe · PayPal
            </Typography>
          </Stack>
        </>
      )}
    </Stack>
  );

  // ── Creator variant ───────────────────────────────────────────────
  if (variant === 'creator') {
    return (
      <Box sx={{ mt: 3 }}>
        <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
              <CheckCircleIcon sx={{ fontSize: 32, opacity: 0.9 }} />
              <Box>
                <Typography variant="h6" fontWeight={700}>Secure Payment Portal</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Pay via balance, spending power, card, or PayPal
                </Typography>
              </Box>
              <Chip
                label={fmt(amt)}
                sx={{ ml: 'auto', fontWeight: 800, fontSize: 14, height: 32, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '& .MuiChip-label': { px: 1.5 } }}
              />
            </Stack>
            <PaymentBody />
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Regular variant ───────────────────────────────────────────────
  return (
    <Box sx={{ mt: 5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
        <Avatar sx={{ width: 42, height: 42, background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
          <WalletIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>Pay This Invoice</Typography>
          <Typography variant="caption" color="text.secondary">
            {isAuthenticated ? 'Choose your payment method' : 'Pay securely — no account required'}
          </Typography>
        </Box>
        <Chip
          label={fmt(amt)}
          sx={{ ml: 'auto', fontWeight: 800, fontSize: 14, height: 32, background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', '& .MuiChip-label': { px: 1.5 } }}
        />
      </Stack>
      <PaymentBody />
    </Box>
  );
};

export default PaymentCTA;